import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckSquare,
  Clock,
  Edit,
  Eye,
  FileText,
  HelpCircle,
  Inbox,
  Info,
  User,
  Workflow,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import type { FormDTO } from "../../domain/contracts";
import {
  useFormFieldsQuery,
  useFormQuery,
  useFormVersionsQuery,
} from "../api/queries";
import { FormDetailTour, useFormDetailTour } from "../tour/FormDetailTour";

// Dynamic color palette for categories
const categoryColorPalette: readonly string[] = [
  "bg-blue-100 text-blue-800",
  "bg-purple-100 text-purple-800",
  "bg-orange-100 text-orange-800",
  "bg-green-100 text-green-800",
  "bg-pink-100 text-pink-800",
  "bg-indigo-100 text-indigo-800",
  "bg-yellow-100 text-yellow-800",
  "bg-cyan-100 text-cyan-800",
  "bg-rose-100 text-rose-800",
  "bg-emerald-100 text-emerald-800",
  "bg-amber-100 text-amber-800",
  "bg-violet-100 text-violet-800",
];

/**
 * Generates a consistent color for a category based on its name
 * Uses a simple hash function to map category names to colors
 */
function getCategoryColor(categoryName: string | null | undefined): string {
  if (!categoryName) {
    return "bg-gray-100 text-gray-800";
  }

  // Simple hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    const char = categoryName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get index
  const index = Math.abs(hash) % categoryColorPalette.length;
  const color = categoryColorPalette[index];
  return color ?? "bg-gray-100 text-gray-800";
}

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  under_revision: "ກຳລັງປັບປຸງ",
  discontinued: "ຢຸດໃຊ້ງານ",
  draft: "ຮ່າງ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  under_revision: "secondary",
  discontinued: "destructive",
  draft: "outline",
};

type FormVersion = {
  id: string;
  version: string;
  changelog: string | null;
  isCurrent: boolean;
  createdAt: Date | string;
};

export function FormDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ from: "/app/forms/$id" });

  const { data: form, ...result } = useFormQuery(id ?? "");
  const formData = form as FormDTO | undefined;
  const canEdit = useActionPermission([Permissions.forms.update]);
  const tour = useFormDetailTour();

  // Fetch form fields for preview
  const { data: fieldsData } = useFormFieldsQuery(
    id ?? "",
    formData?.currentVersionId ?? null,
  );

  // Fetch form versions
  const { data: versionsData } = useFormVersionsQuery(id ?? "");

  return (
    <>
      <Header />
      <Main>
        <FormDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <QueryState
          result={result}
          variant="fullscreen"
          isEmpty={!formData}
          emptyFallback={
            <div className="text-center">
              <p className="text-muted-foreground">ບໍ່ພົບຟອມ</p>
            </div>
          }
        >
          {formData && (
            <div className="space-y-6">
              {/* Back Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  nav({
                    to: "/app/forms",
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined as
                        | Array<{ field: string; dir: "asc" | "desc" }>
                        | undefined,
                      filters: undefined as
                        | Array<{ field: string; op: string; value: unknown }>
                        | undefined,
                    },
                  })
                }
                className="mb-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບຄືນ
              </Button>

              {/* Form Header */}
              <div
                className="flex items-start justify-between"
                data-tourid="detail-header"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="font-bold text-3xl">{formData.name}</h1>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 rounded-full"
                      onClick={tour.startTour}
                    >
                      <HelpCircle className="size-5" />
                    </Button>
                    <Badge variant={statusColors[formData.status]}>
                      {statusLabels[formData.status] || formData.status}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-muted-foreground text-sm">
                    <span>ລະຫັດຟອມ: {formData.id}</span>
                    {formData.categoryName && (
                      <>
                        <span>•</span>
                        <Badge
                          className={getCategoryColor(formData.categoryName)}
                          variant="secondary"
                        >
                          {formData.categoryName}
                        </Badge>
                      </>
                    )}
                    {formData.departmentName && (
                      <>
                        <span>•</span>
                        <span>ພະແນກ: {formData.departmentName}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>ສ້າງເມື່ອ: {formatDateLocal(formData.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {fieldsData?.fields && fieldsData.fields.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        nav({
                          to: "/app/forms/$id/preview",
                          params: { id: formData.id },
                        })
                      }
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      ເບິ່ງຕົວຢ່າງ
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      nav({
                        to: "/app/forms/$id/submissions",
                        params: { id: formData.id },
                        search: {
                          offset: 0,
                          limit: 20,
                          sort: undefined as
                            | Array<{ field: string; dir: "asc" | "desc" }>
                            | undefined,
                          filters: undefined as
                            | Array<{
                                field: string;
                                op: string;
                                value: unknown;
                              }>
                            | undefined,
                        },
                      })
                    }
                  >
                    <Inbox className="mr-2 h-4 w-4" />
                    ລາຍການສົ່ງ
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      nav({
                        to: "/app/forms/$id/analysis",
                        params: { id: formData.id },
                      })
                    }
                  >
                    <BarChart3 className="mr-2 h-4 w-4" />
                    ວິເຄາະ
                  </Button>
                  {canEdit && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() =>
                        nav({
                          to: "/app/forms/$id/edit",
                          params: { id: formData.id },
                        })
                      }
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      ແກ້ໄຂຟອມ
                    </Button>
                  )}
                </div>
              </div>

              {/* Main Content - Two Column Layout */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Left Column */}
                <div className="space-y-6 lg:col-span-2">
                  {/* Form Instructions & Purpose */}
                  {(formData.description ||
                    formData.useCase ||
                    formData.targetAudience ||
                    formData.processAfterSubmission) && (
                    <Card data-tourid="detail-instructions">
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <Info className="h-5 w-5" />
                          <CardTitle>ຄຳແນະນຳແລະຈຸດປະສົງຂອງຟອມ</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {formData.description && (
                          <div>
                            <h3 className="mb-2 font-semibold">ບໍລິບົດການນຳໃຊ້</h3>
                            <p className="text-muted-foreground text-sm">
                              {formData.description}
                            </p>
                          </div>
                        )}

                        {formData.useCase && (
                          <div>
                            <div className="mb-2 flex items-center gap-2">
                              <CheckSquare className="h-4 w-4" />
                              <h3 className="font-semibold">ໃຊ້ກັບກໍລະນີໃດ</h3>
                            </div>
                            <p className="ml-6 text-muted-foreground text-sm">
                              {formData.useCase}
                            </p>
                          </div>
                        )}

                        {formData.targetAudience && (
                          <div>
                            <div className="mb-2 flex items-center gap-2">
                              <User className="h-4 w-4" />
                              <h3 className="font-semibold">ກຸ່ມເປົ້າໝາຍ</h3>
                            </div>
                            <p className="ml-6 text-muted-foreground text-sm">
                              {formData.targetAudience}
                            </p>
                          </div>
                        )}

                        {formData.processAfterSubmission && (
                          <div>
                            <div className="mb-2 flex items-center gap-2">
                              <Workflow className="h-4 w-4" />
                              <h3 className="font-semibold">ຂະບວນການຫຼັງສົ່ງ</h3>
                            </div>
                            <p className="ml-6 text-muted-foreground text-sm">
                              {formData.processAfterSubmission}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Form Fields Preview */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        <CardTitle>ຕົວຢ່າງຟອມ</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {!formData.currentVersionId ? (
                          <p className="text-center text-muted-foreground">
                            ຍັງບໍ່ມີເວີຊັນຟອມ. ກະລຸນາແກ້ໄຂຟອມເພື່ອເພີ່ມຊ່ອງຂໍ້ມູນ.
                          </p>
                        ) : !fieldsData?.fields ? (
                          <p className="text-center text-muted-foreground">
                            ກຳລັງໂຫຼດຊ່ອງຂໍ້ມູນ...
                          </p>
                        ) : fieldsData.fields.length === 0 ? (
                          <p className="text-center text-muted-foreground">
                            ຍັງບໍ່ມີຊ່ອງຂໍ້ມູນ
                          </p>
                        ) : (
                          (
                            fieldsData.fields as Array<{
                              id: string;
                              label: string;
                              fieldType: string;
                              hint: string | null;
                              isRequired: boolean;
                              options: unknown;
                              settings: unknown;
                            }>
                          ).map((field) => {
                            const fieldInputId = `field-${field.id}`;
                            return (
                              <div key={field.id} className="space-y-2">
                                <label
                                  htmlFor={fieldInputId}
                                  className="block font-medium text-sm"
                                >
                                  {field.label}
                                  {field.isRequired && (
                                    <span className="ml-1 text-destructive">
                                      *
                                    </span>
                                  )}
                                </label>
                                {field.hint && (
                                  <p className="text-muted-foreground text-xs">
                                    {field.hint}
                                  </p>
                                )}
                                <div className="rounded-md border bg-muted/50 p-3">
                                  {renderFieldPreview(
                                    {
                                      fieldType: field.fieldType,
                                      options: field.options,
                                      settings: field.settings,
                                      hint: field.hint,
                                    },
                                    fieldInputId,
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <Card data-tourid="detail-stats">
                    <CardHeader>
                      <CardTitle>ສະຖິຕິ</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <FileText className="h-3 w-3" />
                          <span>ຈຳນວນການໃຊ້</span>
                        </div>
                        <p className="font-bold text-2xl">
                          {formData.usageCount.toLocaleString()}
                        </p>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <Eye className="h-3 w-3" />
                          <span>ການເບິ່ງເຫັນ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formData.departmentName
                            ? `ພຽງແຕ່ ${formData.departmentName}`
                            : "ພະນັກງານພາຍໃນເທົ່ານັ້ນ"}
                        </p>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <CheckSquare className="h-3 w-3" />
                          <span>ອະນຸຍາດແນບຟາຍ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formData.allowFileAttachment ? "ແມ່ນ" : "ບໍ່"}
                        </p>
                      </div>

                      {formData.isRecommended && (
                        <div>
                          <Badge
                            variant="default"
                            className="w-full justify-center"
                          >
                            ⭐ ແນະນຳ
                          </Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Metadata */}
                  <Card data-tourid="detail-metadata">
                    <CardHeader>
                      <CardTitle>ຂໍ້ມູນເພີ່ມເຕີມ</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <User className="h-3 w-3" />
                          <span>ສ້າງໂດຍ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formData.createdByName || formData.createdBy}
                        </p>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <Calendar className="h-3 w-3" />
                          <span>ສ້າງເມື່ອ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formatDateTimeLocal(formData.createdAt)}
                        </p>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <Clock className="h-3 w-3" />
                          <span>ອັບເດດລ່າສຸດ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formatDateTimeLocal(formData.updatedAt)}
                        </p>
                      </div>

                      {formData.currentVersionNumber && (
                        <div>
                          <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                            <FileText className="h-3 w-3" />
                            <span>ເວີຊັນປັດຈຸບັນ</span>
                          </div>
                          <div className="space-y-1">
                            <p className="font-medium text-sm">
                              v{formData.currentVersionNumber}
                            </p>
                            {formData.currentVersionChangelog && (
                              <p className="text-muted-foreground text-xs">
                                {formData.currentVersionChangelog}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Form Versions */}
              {versionsData?.versions && versionsData.versions.length > 0 && (
                <Card data-tourid="detail-versions">
                  <CardHeader>
                    <CardTitle>ເວີຊັນທັງໝົດ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Array.isArray(versionsData.versions) &&
                        (versionsData.versions as FormVersion[]).map(
                          (version) => (
                            <div
                              key={version.id}
                              className={`rounded-md border p-4 ${
                                version.isCurrent
                                  ? "border-primary bg-primary/5"
                                  : "bg-muted/50"
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      v{version.version}
                                    </span>
                                    {version.isCurrent && (
                                      <Badge
                                        variant="default"
                                        className="text-xs"
                                      >
                                        ປັດຈຸບັນ
                                      </Badge>
                                    )}
                                  </div>
                                  {version.changelog && (
                                    <p className="mt-2 text-muted-foreground text-sm">
                                      {version.changelog}
                                    </p>
                                  )}
                                  <p className="mt-2 flex items-center gap-1 text-muted-foreground text-xs">
                                    <Calendar className="h-3 w-3" />
                                    {formatDateTimeLocal(version.createdAt)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}

function renderFieldPreview(
  field: {
    fieldType: string;
    options?: unknown;
    settings?: unknown;
    hint?: string | null;
  },
  fieldId?: string,
) {
  // Helper to extract options from field (could be in options or settings.options)
  const getOptions = (): Array<{ label?: unknown; value?: unknown }> | null => {
    // First check if options is directly an array
    if (Array.isArray(field.options)) {
      return field.options as Array<{ label?: unknown; value?: unknown }>;
    }
    // Check if options are in settings
    if (field.settings && typeof field.settings === "object") {
      const settings = field.settings as Record<string, unknown>;
      if (Array.isArray(settings.options)) {
        return settings.options as Array<{ label?: unknown; value?: unknown }>;
      }
    }
    // Debug logging for select/radio fields
    if (field.fieldType === "select" || field.fieldType === "radio") {
      "Field options debug:",
        {
          fieldType: field.fieldType,
          hasOptions: !!field.options,
          hasSettings: !!field.settings,
          optionsType: typeof field.options,
          optionsValue: field.options,
          settingsValue: field.settings,
        };
    }
    return null;
  };

  const options = getOptions();

  switch (field.fieldType) {
    case "text":
    case "email":
    case "phone":
      return (
        <input
          id={fieldId}
          type="text"
          className="w-full bg-transparent text-sm"
          placeholder={field.hint || "ກະລຸນາໃສ່..."}
          disabled
        />
      );
    case "textarea":
      return (
        <textarea
          id={fieldId}
          className="min-h-[80px] w-full resize-none bg-transparent text-sm"
          placeholder={field.hint || "ກະລຸນາໃສ່..."}
          disabled
        />
      );
    case "number":
    case "money":
      return (
        <input
          type="number"
          className="w-full bg-transparent text-sm"
          placeholder={field.hint || "0"}
          disabled
        />
      );
    case "date":
      return (
        <input type="date" className="w-full bg-transparent text-sm" disabled />
      );
    case "datetime":
      return (
        <input
          type="datetime-local"
          className="w-full bg-transparent text-sm"
          disabled
        />
      );
    case "time":
      return (
        <input type="time" className="w-full bg-transparent text-sm" disabled />
      );
    case "select":
    case "multiselect":
      return (
        <select
          className="w-full rounded-md border bg-background px-3 py-2 text-foreground text-sm"
          multiple={field.fieldType === "multiselect"}
        >
          {!options || options.length === 0 ? (
            <option value="" disabled>
              {String(field.hint || "ເລືອກ...")}
            </option>
          ) : (
            <>
              {field.fieldType === "select" && (
                <option value="" className="text-foreground">
                  {String(field.hint || "ເລືອກ...")}
                </option>
              )}
              {options.map(
                (
                  opt: { label?: unknown; value?: unknown },
                  idx: number,
                ): React.ReactElement => {
                  const optValue = String(opt.value ?? idx);
                  const optLabel = String(opt.label ?? opt.value ?? idx);
                  return (
                    <option
                      key={optValue}
                      value={optValue}
                      className="bg-background text-foreground"
                    >
                      {optLabel}
                    </option>
                  );
                },
              )}
            </>
          )}
        </select>
      );
    case "radio":
      return (
        <div className="space-y-2">
          {options
            ? options.map(
                (
                  opt: { label?: unknown; value?: unknown },
                  idx: number,
                ): React.ReactElement => {
                  const optValue = String(opt.value ?? idx);
                  const optLabel = String(opt.label ?? opt.value ?? idx);
                  const radioId = `preview-radio-${optValue}-${idx}`;
                  return (
                    <label
                      key={optValue}
                      htmlFor={radioId}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        id={radioId}
                        type="radio"
                        name={`preview-${field.fieldType}`}
                        value={optValue}
                        disabled
                      />
                      <span>{optLabel}</span>
                    </label>
                  );
                },
              )
            : null}
        </div>
      );
    case "checkbox": {
      const checkboxId = fieldId || `preview-checkbox-${Date.now()}`;
      return (
        <label htmlFor={checkboxId} className="flex items-center gap-2 text-sm">
          <input id={checkboxId} type="checkbox" disabled />
          <span>{field.hint || "ຕິກເພື່ອຢືນຢັນ"}</span>
        </label>
      );
    }
    case "file":
      return (
        <div className="rounded-md border border-dashed p-4 text-center text-muted-foreground text-sm">
          <p>ກົດເພື່ອເລືອກແຟ້ມ</p>
        </div>
      );
    case "signature":
      return (
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground text-sm">
          <p>ລາຍເຊັນ</p>
        </div>
      );
    default:
      return (
        <input
          type="text"
          className="w-full bg-transparent text-sm"
          placeholder={field.hint || "ກະລຸນາໃສ່..."}
          disabled
        />
      );
  }
}
